import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  KeyRound,
  Loader2,
  Plus,
  ShieldCheck,
  Copy,
  RefreshCcw,
  UserRound,
  Truck,
  Package2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

function parseLines(value) {
  return value
    .split(/\r?\n|,/)
    .map((item) => item.trim().toUpperCase())
    .filter(Boolean);
}

function listToTextarea(values) {
  return (values || []).join("\n");
}

function formatDate(value) {
  if (!value) return "Never";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function buildDraft(user) {
  if (!user) {
    return {
      customer_name: "",
      company_name: "",
      email: "",
      phone: "",
      active: true,
      orderNumbersText: "",
      trackingIdsText: "",
    };
  }
  return {
    customer_name: user.customer_name || "",
    company_name: user.company_name || "",
    email: user.email || "",
    phone: user.phone || "",
    active: Boolean(user.active),
    orderNumbersText: listToTextarea(user.order_numbers),
    trackingIdsText: listToTextarea(user.tracking_ids),
  };
}

async function copyText(value, successMessage) {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(successMessage);
  } catch {
    toast.error("Clipboard copy failed");
  }
}

export default function AdminClientPortal() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [draft, setDraft] = useState(buildDraft(null));
  const [issuedCredentials, setIssuedCredentials] = useState(null);
  const [search, setSearch] = useState("");

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["admin-customer-portal-users"],
    queryFn: async () => (await api.get("/admin/customer-portal/users")).data,
  });

  useEffect(() => {
    if (!selected) return;
    const latest = users.find((item) => item.id === selected.id);
    if (latest) setSelected(latest);
  }, [selected, users]);

  useEffect(() => {
    if (selected) {
      setDraft(buildDraft(selected));
    }
  }, [selected]);

  const createMut = useMutation({
    mutationFn: async (payload) => (await api.post("/admin/customer-portal/users", payload)).data,
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ["admin-customer-portal-users"] });
      setIsCreateOpen(false);
      setSelected(result.user);
      setIssuedCredentials(result);
      toast.success("Portal access generated");
    },
    onError: (error) => toast.error(formatApiError(error)),
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, payload }) =>
      (await api.put(`/admin/customer-portal/users/${id}`, payload)).data,
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ["admin-customer-portal-users"] });
      setSelected(result);
      toast.success("Portal user updated");
    },
    onError: (error) => toast.error(formatApiError(error)),
  });

  const resetCodeMut = useMutation({
    mutationFn: async (id) =>
      (await api.post(`/admin/customer-portal/users/${id}/reset-access-code`)).data,
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ["admin-customer-portal-users"] });
      setSelected(result.user);
      setIssuedCredentials(result);
      toast.success("New access code generated");
    },
    onError: (error) => toast.error(formatApiError(error)),
  });

  const filteredUsers = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((item) =>
      [
        item.customer_access_id,
        item.customer_name,
        item.company_name,
        item.email,
        item.phone,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [search, users]);

  const stats = useMemo(
    () => ({
      total: users.length,
      active: users.filter((item) => item.active).length,
      signedIn: users.filter((item) => item.last_login_at).length,
      linkedShipments: users.reduce((sum, item) => sum + (item.tracking_ids?.length || 0), 0),
    }),
    [users]
  );

  const submitCreate = (event) => {
    event.preventDefault();
    createMut.mutate({
      customer_name: draft.customer_name.trim(),
      company_name: draft.company_name.trim(),
      email: draft.email.trim(),
      phone: draft.phone.trim(),
      active: draft.active,
      order_numbers: parseLines(draft.orderNumbersText),
      tracking_ids: parseLines(draft.trackingIdsText),
    });
  };

  const saveSelected = () => {
    if (!selected) return;
    updateMut.mutate({
      id: selected.id,
      payload: {
        customer_name: draft.customer_name.trim(),
        company_name: draft.company_name.trim(),
        email: draft.email.trim(),
        phone: draft.phone.trim(),
        active: draft.active,
        order_numbers: parseLines(draft.orderNumbersText),
        tracking_ids: parseLines(draft.trackingIdsText),
      },
    });
  };

  return (
    <div data-testid="admin-client-portal">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <span className="badge-purple">
            <KeyRound className="h-3.5 w-3.5" /> Client Supply Chain Portal
          </span>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-3 text-[var(--rsi-navy)]">
            Portal Access Control
          </h1>
          <p className="text-gray-600 text-sm mt-1 max-w-3xl">
            Generate customer portal access IDs and access codes from the admin panel, control who can sign in, and decide which orders and shipments each customer can see.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setDraft(buildDraft(null));
            setIsCreateOpen(true);
          }}
          className="btn-primary"
        >
          <Plus className="h-4 w-4" /> Generate portal access
        </button>
      </div>

      {issuedCredentials ? (
        <div className="mb-6 rounded-[28px] border border-[var(--rsi-purple)]/15 bg-[linear-gradient(180deg,#f9f6ff_0%,#f5effd_100%)] p-6 shadow-[0_24px_60px_-40px_rgba(42,19,65,0.34)]">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[var(--rsi-purple)]">
                Secure credential output
              </div>
              <div className="mt-2 font-display text-2xl font-black tracking-tight text-[var(--rsi-navy)]">
                Save and share these portal credentials with the verified buyer.
              </div>
              <div className="mt-3 text-sm text-gray-600 max-w-2xl">
                The access code is shown only when it is newly generated or reset from the admin panel.
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() =>
                  copyText(
                    `Access ID: ${issuedCredentials.user.customer_access_id}\nAccess Code: ${issuedCredentials.access_code}`,
                    "Portal credentials copied"
                  )
                }
                className="btn-primary"
              >
                <Copy className="h-4 w-4" /> Copy credentials
              </button>
            </div>
          </div>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div className="rounded-3xl bg-white/90 p-5 border border-white/70">
              <div className="text-xs uppercase tracking-[0.18em] text-gray-400">Access ID</div>
              <div className="mt-2 font-mono-plex text-lg font-semibold text-[var(--rsi-navy)]">
                {issuedCredentials.user.customer_access_id}
              </div>
            </div>
            <div className="rounded-3xl bg-white/90 p-5 border border-white/70">
              <div className="text-xs uppercase tracking-[0.18em] text-gray-400">Access Code</div>
              <div className="mt-2 font-mono-plex text-lg font-semibold text-[var(--rsi-navy)]">
                {issuedCredentials.access_code}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="rsi-card p-5">
          <div className="text-xs uppercase tracking-wider text-gray-400">Portal Users</div>
          <div className="mt-2 font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)]">{stats.total}</div>
        </div>
        <div className="rsi-card p-5">
          <div className="text-xs uppercase tracking-wider text-gray-400">Active Access</div>
          <div className="mt-2 font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)]">{stats.active}</div>
        </div>
        <div className="rsi-card p-5">
          <div className="text-xs uppercase tracking-wider text-gray-400">Logged In Before</div>
          <div className="mt-2 font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)]">{stats.signedIn}</div>
        </div>
        <div className="rsi-card p-5">
          <div className="text-xs uppercase tracking-wider text-gray-400">Linked Shipments</div>
          <div className="mt-2 font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)]">{stats.linkedShipments}</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search access ID, customer, company, email..."
          className="w-full max-w-md"
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.2fr)_minmax(360px,0.8fr)]">
        <div className="rsi-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--rsi-bg-subtle)] text-left">
                <tr>
                  <th className="p-4 font-semibold text-[var(--rsi-navy)]">Access</th>
                  <th className="p-4 font-semibold text-[var(--rsi-navy)]">Customer</th>
                  <th className="p-4 font-semibold text-[var(--rsi-navy)]">Scope</th>
                  <th className="p-4 font-semibold text-[var(--rsi-navy)]">Status</th>
                  <th className="p-4 font-semibold text-[var(--rsi-navy)]">Last Login</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center">
                      <Loader2 className="h-5 w-5 animate-spin inline text-[var(--rsi-purple)]" />
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-gray-500">
                      No portal users found.
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr
                      key={user.id}
                      onClick={() => setSelected(user)}
                      className="cursor-pointer hover:bg-gray-50"
                      data-testid={`portal-user-row-${user.id}`}
                    >
                      <td className="p-4">
                        <div className="font-mono-plex font-semibold text-[var(--rsi-navy)]">{user.customer_access_id}</div>
                        <div className="text-xs text-gray-500 mt-1">{user.email || "No email linked"}</div>
                      </td>
                      <td className="p-4">
                        <div className="font-semibold text-[var(--rsi-navy)]">{user.customer_name || "Unnamed buyer"}</div>
                        <div className="text-xs text-gray-500 mt-1">{user.company_name || "No company"}</div>
                      </td>
                      <td className="p-4 text-xs text-gray-600">
                        {user.order_numbers?.length || 0} orders · {user.tracking_ids?.length || 0} shipments
                      </td>
                      <td className="p-4">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${
                            user.active ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-600"
                          }`}
                        >
                          {user.active ? "active" : "disabled"}
                        </span>
                      </td>
                      <td className="p-4 text-xs text-gray-500">{formatDate(user.last_login_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rsi-card p-6">
          {selected ? (
            <>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-gray-400">Portal profile</div>
                  <div className="mt-2 font-display text-2xl font-black tracking-tight text-[var(--rsi-navy)]">
                    {selected.customer_name || "Client portal user"}
                  </div>
                  <div className="mt-1 font-mono-plex text-sm text-gray-500">{selected.customer_access_id}</div>
                </div>
                <span
                  className={`inline-flex rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${
                    draft.active ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {draft.active ? "active" : "disabled"}
                </span>
              </div>

              <div className="mt-5 grid gap-4">
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider">Customer name</label>
                  <Input
                    value={draft.customer_name}
                    onChange={(event) => setDraft((prev) => ({ ...prev, customer_name: event.target.value }))}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider">Company name</label>
                  <Input
                    value={draft.company_name}
                    onChange={(event) => setDraft((prev) => ({ ...prev, company_name: event.target.value }))}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider">Email</label>
                  <Input
                    value={draft.email}
                    onChange={(event) => setDraft((prev) => ({ ...prev, email: event.target.value }))}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider">Phone</label>
                  <Input
                    value={draft.phone}
                    onChange={(event) => setDraft((prev) => ({ ...prev, phone: event.target.value }))}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider">Linked order numbers</label>
                  <Textarea
                    rows={4}
                    value={draft.orderNumbersText}
                    onChange={(event) => setDraft((prev) => ({ ...prev, orderNumbersText: event.target.value.toUpperCase() }))}
                    placeholder="One order number per line"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider">Linked tracking IDs</label>
                  <Textarea
                    rows={4}
                    value={draft.trackingIdsText}
                    onChange={(event) => setDraft((prev) => ({ ...prev, trackingIdsText: event.target.value.toUpperCase() }))}
                    placeholder="One tracking ID per line"
                    className="mt-1.5"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setDraft((prev) => ({ ...prev, active: !prev.active }))}
                    className="btn-ghost"
                  >
                    <ShieldCheck className="h-4 w-4" /> {draft.active ? "Disable portal access" : "Enable portal access"}
                  </button>
                  <button
                    type="button"
                    onClick={() => resetCodeMut.mutate(selected.id)}
                    className="btn-ghost"
                    disabled={resetCodeMut.isPending}
                  >
                    <RefreshCcw className="h-4 w-4" /> {resetCodeMut.isPending ? "Generating..." : "Reset access code"}
                  </button>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={saveSelected}
                    className="btn-primary"
                    disabled={updateMut.isPending}
                  >
                    {updateMut.isPending ? "Saving..." : "Save portal profile"}
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      copyText(
                        `Access ID: ${selected.customer_access_id}`,
                        "Access ID copied"
                      )
                    }
                    className="btn-ghost"
                  >
                    <Copy className="h-4 w-4" /> Copy access ID
                  </button>
                </div>

                <div className="grid sm:grid-cols-3 gap-3 pt-2">
                  <div className="rounded-2xl bg-[var(--rsi-bg-subtle)] p-4">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400">Customer</div>
                    <div className="mt-3 flex items-center gap-2 text-sm font-semibold text-[var(--rsi-navy)]">
                      <UserRound className="h-4 w-4 text-[var(--rsi-purple)]" />
                      {selected.customer_name || "Unnamed"}
                    </div>
                  </div>
                  <div className="rounded-2xl bg-[var(--rsi-bg-subtle)] p-4">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400">Orders</div>
                    <div className="mt-3 flex items-center gap-2 text-sm font-semibold text-[var(--rsi-navy)]">
                      <Package2 className="h-4 w-4 text-[var(--rsi-purple)]" />
                      {selected.order_numbers?.length || 0} linked
                    </div>
                  </div>
                  <div className="rounded-2xl bg-[var(--rsi-bg-subtle)] p-4">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400">Shipments</div>
                    <div className="mt-3 flex items-center gap-2 text-sm font-semibold text-[var(--rsi-navy)]">
                      <Truck className="h-4 w-4 text-[var(--rsi-purple)]" />
                      {selected.tracking_ids?.length || 0} linked
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-[28px] border border-dashed border-gray-200 p-8 text-center">
              <div className="mx-auto h-14 w-14 rounded-2xl bg-[var(--rsi-bg-subtle)] grid place-items-center text-[var(--rsi-purple)]">
                <KeyRound className="h-6 w-6" />
              </div>
              <div className="mt-4 font-display text-2xl font-black tracking-tight text-[var(--rsi-navy)]">
                Select a portal user
              </div>
              <div className="mt-2 text-sm text-gray-600">
                Open a portal profile to manage linked orders, linked shipments, access state, and credential resets.
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold tracking-tight text-[var(--rsi-navy)]">
              Generate client portal access
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitCreate} className="grid gap-4 mt-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider">Customer name</label>
                <Input
                  value={draft.customer_name}
                  onChange={(event) => setDraft((prev) => ({ ...prev, customer_name: event.target.value }))}
                  className="mt-1.5"
                />
              </div>
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider">Company name</label>
                <Input
                  value={draft.company_name}
                  onChange={(event) => setDraft((prev) => ({ ...prev, company_name: event.target.value }))}
                  className="mt-1.5"
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider">Email</label>
                <Input
                  value={draft.email}
                  onChange={(event) => setDraft((prev) => ({ ...prev, email: event.target.value }))}
                  className="mt-1.5"
                />
              </div>
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider">Phone</label>
                <Input
                  value={draft.phone}
                  onChange={(event) => setDraft((prev) => ({ ...prev, phone: event.target.value }))}
                  className="mt-1.5"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wider">Order numbers</label>
              <Textarea
                rows={3}
                value={draft.orderNumbersText}
                onChange={(event) => setDraft((prev) => ({ ...prev, orderNumbersText: event.target.value.toUpperCase() }))}
                placeholder="Optional. One per line."
                className="mt-1.5"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wider">Tracking IDs</label>
              <Textarea
                rows={3}
                value={draft.trackingIdsText}
                onChange={(event) => setDraft((prev) => ({ ...prev, trackingIdsText: event.target.value.toUpperCase() }))}
                placeholder="Optional. One per line."
                className="mt-1.5"
              />
            </div>
            <div className="flex flex-wrap gap-3 pt-2">
              <button type="submit" className="btn-primary" disabled={createMut.isPending}>
                {createMut.isPending ? "Generating..." : "Generate portal credentials"}
              </button>
              <button type="button" className="btn-ghost" onClick={() => setIsCreateOpen(false)}>
                Cancel
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
